import { useEffect, useRef } from "react";
import useGameSounds from "./useGameSounds";
import useKidVoice from "./useKidVoice";

/**
 * useEntrySound — plays a cheerful welcome chime (and an optional spoken
 * phrase) ONCE when a kids page is opened, to make the section feel alive and
 * inviting.
 *
 * Browsers block audio until the user interacts with the page. If audio is
 * already unlocked (e.g. the child navigated here from another kids page in
 * the same SPA session) the welcome plays right away; otherwise it fires on
 * the very first tap / click / key press.
 *
 * IMPORTANT: this must fire only once per mount. The sound hooks return a new
 * object literal every render, so we keep the latest values in a ref and run
 * the effect with an empty dependency array — otherwise the greeting would
 * replay on every re-render (e.g. each PIN digit tap).
 */
export default function useEntrySound(phrase) {
  const sounds    = useGameSounds();
  const { speak } = useKidVoice();

  // Always hold the freshest references without retriggering the effect.
  const ref = useRef({ sounds, speak, phrase });
  ref.current = { sounds, speak, phrase };

  useEffect(() => {
    // Only greet once per browser tab session per distinct phrase — without
    // this, returning to a page (e.g. the game hub after finishing a game)
    // re-mounts the component and would replay the same greeting every time.
    const sessionKey = ref.current.phrase ? `kids_entry_sound_${ref.current.phrase}` : null;
    if (sessionKey && sessionStorage.getItem(sessionKey)) return undefined;

    let played = false;

    const cleanup = () => {
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("touchstart", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
    };

    const fire = () => {
      if (played) return;
      played = true;
      const { sounds: s, speak: sp, phrase: ph } = ref.current;
      try { s.playComplete(false); } catch { /* ignore */ }
      if (ph) setTimeout(() => { try { sp(ph); } catch { /* ignore */ } }, 450);
      if (sessionKey) sessionStorage.setItem(sessionKey, "1");
      cleanup();
    };

    const onGesture = () => fire();

    // Is audio already unlocked? (true after any earlier user gesture this doc)
    let unlocked = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const probe = new AC();
        unlocked = probe.state === "running";
        if (probe.close) probe.close();
      }
    } catch { /* ignore */ }

    if (unlocked) {
      fire();
    } else {
      window.addEventListener("pointerdown", onGesture, true);
      window.addEventListener("touchstart", onGesture, true);
      window.addEventListener("keydown", onGesture, true);
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run exactly once per page mount
}
